<template>
    <div>
        <div style="width: 50%; margin: 3em auto; text-align: left; ">
            <h4>Demographic File Scrubber</h4>
            This page will scrub, validate, and standardize the format of a demographics file received by the BenComms team.
            <br /><br />
            The first step is to replace any email addresses with commas(,) with periods(.)<br /><br />
            Next it looks for data columns by name (before searching it removes all non-alpha characters):<br />
            <ul>
                <li>First Name (firstname, givenname, fullname, employeename, name)</li>
                <li>Last Name (lastname, surname, fullname, employeename, name)</li>
                <li>Email Address (emailaddress, email, workemail, homeemail, personalemail)</li>
                <li>Phone Number (phonenumber, phone, homephone, mobilephone, cellphone, workphone)</li>
            </ul>
            Finally it will validate that a phone number and email address are valid.  
        </div>
        <input type="file" @change="handleFileUpload" />
        <div v-if="fileProcessed" style="font-size: 30px; margin: 1em auto; width: 70%; max-width: 500px;">
            Results
            <div style="display: flex; flex-direction: row; justify-content: space-between;">
                <div v-if="outputFileUrl">
                    <a :href="outputFileUrl" download="output.csv" style="color: green"><i class="fa-sharp fa-solid fa-cloud-arrow-down"></i><br />Scrubbed File</a>
                </div>

                <div v-if="errorFileUrl">
                    <a :href="errorFileUrl" download="errors.csv" style="color: red"><i class="fa-sharp fa-solid fa-cloud-arrow-down"></i><br />Error File</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default { 
    name: 'FileScrubber',
   // components: {ItemModal, ListingTable},
    data() {
        return {
        processedContent: null,
        columnMappings: {
            firstName: null,
            lastName: null,
            fullName: null,
            emailAddress: null,
            phoneNumber: null
        },
        errors: [],
        output: [],
        fileProcessed: false,
        outputFileUrl: null,
        errorFileUrl: null
    };
  },
  methods: {
    // Handle file upload
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file && file.type === 'text/csv') {
        this.readCSVFile(file);
      } else {
        alert('Please upload a valid CSV file');
      }
    },
    // Read and process CSV file
    readCSVFile(file) {
        this.errors = [];  
        this.output = [];  
        this.outputFileUrl = null;
        this.errorFileUrl = null;
        this.fileProcessed = false;
        this.output.push(["First Name", "Last Name", "Email", "Phone"]);  
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            const lines = content.split('\n');
            
            // Assume first row is header, process each subsequent row
            this.getColumnMappings(lines[0].split(','));
       
            // Process each record (excluding the header)
            for (let i = 1; i < lines.length; i++) {
                //replace , in email address here.   If we don't do it here, we will screw up the split on comma.
                const line = lines[i].replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+),([a-zA-Z]{2,})/g, 
                        (match, emailPart, domainPart) => {
                    // Replace commas inside the email address (before the domain part) with periods
                    const fixedEmail = emailPart.replace(/,/g, '.') + '.' + domainPart;
                    return fixedEmail;
                });

                const row = line.split(',');
                var firstName = this.getFirstName(row);
                var lastName = this.getLastName(row);
                var emailAddress = row[this.columnMappings.emailAddress];
                var phoneNumber = row[this.columnMappings.phoneNumber];

                try {
                    
                    if(!firstName) throw new Error("No first name");
                    if(!lastName) throw new Error("No last name");
                    if(!emailAddress) throw new Error("No email address");
                    if(!phoneNumber) throw new Error("No phone number");
                
                    emailAddress = this.scrubAndValidateEmail(emailAddress);
                    phoneNumber = this.scrubAndValidatePhone(phoneNumber);

                    this.output.push([ firstName, lastName, emailAddress, phoneNumber]);
                } catch (error) {
                    this.errors.push([i, error.message]);
                }

            }

            if(this.output.length > 0)
                this.outputFileUrl = this.createDownloadUrl(this.arrayToCSV(this.output),  'text/csv');

            if(this.errors.length > 0)
                this.errorFileUrl = this.createDownloadUrl(this.arrayToCSV(this.errors),  'text/csv');
        };
      
        reader.readAsText(file);
        this.fileProcessed = true;
    },
    arrayToCSV(data) {
        return data.map(row =>
            row.map(value => {
                // Convert to string and escape double quotes
                let str = String(value).replace(/"/g, '""');
                return `"${str}"`; // Wrap in double quotes
            }).join(",") 
        ).join("\n"); 
    },
    createDownloadUrl(content, mimeType) {
        const blob = new Blob([content], { type: mimeType });
        return URL.createObjectURL(blob);
    },    
    getFirstName(rowArray) {
        const fieldIndex = this.columnMappings.firstName !== null ? this.columnMappings.firstName : this.columnMappings.fullName;
        if(fieldIndex !== null)
            return rowArray[fieldIndex].split(" ")[0];
        else 
            return "";
    },
    scrubAndValidateEmail(email) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        email = email.replaceAll("con", "com");  
        if(!emailRegex.test(email)) {
            throw new Error("Email failed validation");
        }

        return email;
    },
    scrubAndValidatePhone(phone) {
        phone = phone.replace(/\D/g, "");
        const phoneRegex = /^(1[-.\s]?)?([2-9][0-9]{2})[-.\s]?([2-9][0-9]{2})[-.\s]?([0-9]{4})$/;
        if(!phoneRegex.test(phone)) {
            throw new Error("Phone failed validation");
        }
        return phone;
    },
    getLastName(rowArray) {
        if(this.columnMappings.lastName !== null)
            return rowArray[this.columnMappings.lastName];
        else if(this.columnMappings.fullName !== null) {
            const fieldArray = rowArray[this.columnMappings.fullName].split(" ");
            if(fieldArray.length > 1)
                return fieldArray.slice(1).join(" ");
        }

        return ""; 
    },
    getColumnMappings(headerArray) {
        const updatedArr = headerArray.map(item => 
            item
                .replace(/[^a-zA-Z]/g, '')  // Removes anything but letters
                .toLowerCase()           // Converts to lowercase
            );

        this.columnMappings.firstName = this.searchForColumns(updatedArr, ["firstname", "givenname"], true);
        this.columnMappings.lastName = this.searchForColumns(updatedArr, ["lastname", "surname"], true);
        if(!this.columnMappings.firstName && !this.columnMappings.lastName) {
            this.columnMappings.fullName = this.searchForColumns(updatedArr, ["fullname", "employeename"], true);
        
            if(!this.columnMappings.fullName && !this.columnMappings.lastName && !this.columnMappings.firstName )
                this.columnMappings.fullName = this.searchForColumns(updatedArr, ["name"], false);
        }

        this.columnMappings.emailAddress = this.searchForColumns(updatedArr, 
                ["emailaddress", "email", "workemail", "homeemail", "personalemail"], true);
        
        this.columnMappings.phoneNumber = this.searchForColumns(updatedArr, ["phonenumber"], true);

        if(!this.columnMappings.phoneNumber) 
            this.columnMappings.phoneNumber = this.searchForColumns(updatedArr, ["phone"], false);

        if(!this.columnMappings.phoneNumber) 
            this.columnMappings.phoneNumber = this.searchForColumns(updatedArr, ["homephone", "mobilephone", "cellphone", "workphone"], true);
    },
    searchForColumns(headerArray, searchStrings, useLikeSearch) { 
        for (let searchString of searchStrings) {
            const foundIndex = 
                useLikeSearch ? headerArray.findIndex(item => item.includes(searchString)) :
                              headerArray.findIndex(item => item === searchString);

            if(foundIndex >= 0)
                return foundIndex;            
        }

        return null;
    },
  }
}
</script>
